/**
 * User turn — rendered as a subtle right-aligned grey card, no avatar,
 * no role marker. The right alignment delineates user input from
 * assistant output without the heavy chat-bubble look; the symmetric
 * top/bottom margin gives each user turn a visible "section break"
 * around it so the conversation reads as discrete prompt -> response
 * chunks instead of a wall of bubbles colliding.
 *
 * Inline image / document blocks that arrive as structured content
 * render below the text bubble. Files uploaded through the
 * composer arrive inside a leading ``<attachments>`` sentinel block;
 * we strip that block from the displayed text and render a compact
 * filename chip per path.
 */

import type { LucideIcon } from 'lucide-react';
import { Compass, Cpu, FileText, Folder, Play, Wrench } from 'lucide-react';
import * as React from 'react';
import type { Turn } from '@/lib/agent/reducer';
import { turnBlocks } from '@/lib/agent/reducer';
import type { AgentStarterPromptIcon } from '@/lib/agent/starterPrompts';
import { cn } from '@/lib/utils/cn';
import { useAgentImagePreview } from './AgentImagePreview';
import { attachmentDisplayName, attachmentUploadUrl, isImageAttachment } from './attachmentUtils';
import { DocumentBlockView } from './blocks/DocumentBlockView';
import { ImageBlockView } from './blocks/ImageBlockView';
import { confirmedMentionRanges, DecoratedInputText, inputTokenDecorations } from './inputDecorations';

export interface UserPromptProps {
  turn: Turn;
  compactTop?: boolean;
  className?: string;
}

interface ParsedText {
  attachments: string[];
  contextPaths: string[];
  body: string;
}

const STARTER_ICONS: Record<AgentStarterPromptIcon, LucideIcon> = {
  compass: Compass,
  play: Play,
  wrench: Wrench,
  cpu: Cpu,
};

/**
 * Strip leading sentinel blocks inserted by the composer. Uploaded
 * files use ``<attachments>``; ``@`` file/folder mentions use
 * ``<context_paths>``. The visible body keeps the user's original
 * prompt text, including the typed ``@path`` mention.
 */
function parseLeadingContextBlocks(raw: string): ParsedText {
  let body = raw;
  const attachments: string[] = [];
  const contextPaths: string[] = [];

  for (;;) {
    const attachment = body.match(/^<attachments>\n([\s\S]*?)\n<\/attachments>(?:\n\n|\n|$)/);
    if (attachment) {
      attachments.push(...blockLines(attachment[1] ?? ''));
      body = body.slice(attachment[0].length);
      continue;
    }

    const context = body.match(/^<context_paths>\n([\s\S]*?)\n<\/context_paths>(?:\n\n|\n|$)/);
    if (context) {
      contextPaths.push(...blockLines(context[1] ?? ''));
      body = body.slice(context[0].length);
      continue;
    }

    return { attachments, contextPaths, body };
  }
}

function blockLines(raw: string): string[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export const UserPrompt = React.memo(function UserPrompt({ turn, compactTop = false, className }: UserPromptProps) {
  const blocks = turnBlocks(turn);
  const rawText = blocks
    .map((b) => (b.block_type === 'TextBlock' ? b.text : ''))
    .filter(Boolean)
    .join('\n');
  const { attachments, contextPaths, body } = parseLeadingContextBlocks(rawText);
  const images = blocks.filter((b) => b.block_type === 'ImageBlock');
  const documents = blocks.filter((b) => b.block_type === 'DocumentBlock');
  const starterDisplay = turn.display?.kind === 'starter_prompt' ? turn.display : null;

  if (
    !starterDisplay &&
    !body &&
    images.length === 0 &&
    documents.length === 0 &&
    attachments.length === 0 &&
    contextPaths.length === 0
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        // The chat column's flex ``gap-3`` already separates every
        // pair of turns; user prompts get extra margin for normal
        // prompt -> response boundaries. Consecutive user bubbles
        // pull back against that parent gap so quick follow-ups read
        // like a tight chat stack.
        'mb-2 flex flex-col items-end gap-1.5',
        compactTop ? '-mt-3.5' : 'mt-2',
        className
      )}
    >
      {attachments.length > 0 && (
        <div className="flex max-w-full flex-wrap justify-end gap-1.5">
          {attachments.map((path, i) => (
            <AttachedFileChip key={`att-${i}`} path={path} />
          ))}
        </div>
      )}
      {contextPaths.length > 0 && (
        <div className="flex max-w-full flex-wrap justify-end gap-1.5">
          {contextPaths.map((path, i) => (
            <ContextPathChip key={`ctx-${i}`} path={path} />
          ))}
        </div>
      )}
      {starterDisplay && <StarterPromptCard display={starterDisplay} />}
      {!starterDisplay && body && <UserPromptBody body={body} contextPaths={contextPaths} />}
      {images.length > 0 && (
        <div className="flex max-w-full flex-wrap justify-end gap-1.5">
          {images.map((b, i) =>
            b.block_type === 'ImageBlock' ? <ImageBlockView key={`img-${i}`} block={b} dense /> : null
          )}
        </div>
      )}
      {documents.length > 0 && (
        <div className="flex max-w-full flex-col items-end gap-1">
          {documents.map((b, i) =>
            b.block_type === 'DocumentBlock' ? <DocumentBlockView key={`doc-${i}`} block={b} /> : null
          )}
        </div>
      )}
    </div>
  );
});

/**
 * Body of a user-prompt bubble with shared slash and @path decorations.
 *
 * Reuses :func:`inputTokenDecorations` and :class:`DecoratedInputText`
 * from the composer so a sent ``/compact`` or ``@scenes/...`` carries
 * the same chip styling the user saw while typing. ``@path`` mentions are
 * only decorated when the token is backed by a ``<context_paths>`` entry,
 * mirroring the composer's picker-confirmed contract -- a pasted
 * ``@Linear`` with no context path stays plain. The plain-body path is
 * kept for the common case where no decorations exist, so we don't pay
 * the wrapping/span cost on every chat bubble.
 */
function UserPromptBody({ body, contextPaths }: { body: string; contextPaths: string[] }) {
  const decorations = React.useMemo(
    () => inputTokenDecorations(body, confirmedMentionRanges(body, contextPaths)),
    [body, contextPaths]
  );
  return (
    <div className="max-w-[85%] select-text rounded-2xl bg-gray-100 px-4 py-2.5 text-[14px] text-gray-900 leading-relaxed dark:bg-white/5 dark:text-gray-100">
      <pre className="m-0 whitespace-pre-wrap break-words font-sans">
        {decorations.length > 0 ? <DecoratedInputText value={body} decorations={decorations} /> : body}
      </pre>
    </div>
  );
}

function StarterPromptCard({ display }: { display: NonNullable<Turn['display']> & { kind: 'starter_prompt' } }) {
  const Icon = STARTER_ICONS[display.iconKey];
  const description = display.description.trim();
  return (
    <div
      className={cn(
        'flex max-w-[420px] items-start gap-3 rounded-xl border bg-white/80 px-3.5 py-2.5 text-left shadow-[0_10px_28px_-24px_rgba(15,23,42,0.45)]',
        'border-gray-200/80 dark:border-white/[0.08] dark:bg-white/[0.04] dark:shadow-none'
      )}
      title="Sent starter prompt"
    >
      <span
        className={cn(
          'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-500 ring-1 ring-blue-100/80',
          'dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-300/15'
        )}
      >
        <Icon className="size-3.5" strokeWidth={1.75} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-medium text-[12.5px] text-gray-700 dark:text-gray-200">{display.label}</span>
        {description && (
          <span className="mt-0.5 text-[11.5px] text-gray-500 leading-snug dark:text-gray-400">{description}</span>
        )}
      </span>
    </div>
  );
}

/** Compact chip rendered above a user turn for each uploaded attachment. */
function AttachedFileChip({ path }: { path: string }) {
  const descriptor = { name: attachmentDisplayName(path), path };
  const showImage = isImageAttachment(descriptor);
  const url = attachmentUploadUrl(path);
  const imagePreview = useAgentImagePreview();

  function openAttachment() {
    if (showImage && imagePreview) {
      imagePreview.openImage({ src: url, alt: descriptor.name, caption: descriptor.name });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <button
      type="button"
      onClick={openAttachment}
      className={cn(
        'flex max-w-[260px] cursor-pointer items-center gap-2 overflow-hidden rounded-lg border bg-white px-2 py-1.5 text-left transition-colors',
        'border-gray-200 hover:bg-gray-50 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]'
      )}
      title={path}
    >
      {showImage ? (
        <img
          src={url}
          alt=""
          className={cn('size-7 shrink-0 rounded-md object-cover', 'border border-gray-200 dark:border-white/10')}
        />
      ) : (
        <div
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-md',
            'bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400'
          )}
        >
          <FileText className="size-3.5" />
        </div>
      )}
      <div className="truncate text-[13px] text-gray-800 leading-tight dark:text-gray-100">
        {attachmentDisplayName(path)}
      </div>
    </button>
  );
}

function ContextPathChip({ path }: { path: string }) {
  const isDirectoryHint = path.endsWith('/');
  return (
    <div
      className={cn(
        'flex max-w-[260px] items-center gap-2 overflow-hidden rounded-lg border bg-white px-2 py-1.5',
        'border-emerald-200/80 dark:border-emerald-300/20 dark:bg-emerald-400/[0.04]'
      )}
      title={path}
    >
      <span className="shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 font-medium text-[11px] text-emerald-700 leading-none ring-1 ring-emerald-200/80 dark:bg-emerald-400/10 dark:text-emerald-200 dark:ring-emerald-300/20">
        @
      </span>
      <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-200">
        {isDirectoryHint ? <Folder className="size-3.5" /> : <FileText className="size-3.5" />}
      </div>
      <div className="truncate text-[13px] text-gray-800 leading-tight dark:text-gray-100">
        {attachmentDisplayName(path)}
      </div>
    </div>
  );
}
